"use client";
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  createContext,
  useMemo,
  useContext,
} from "react";
import { useHubspotIdentify } from "./hooks/use-hubspot-identify";

// Add type declaration for HubSpot queue
declare global {
  interface Window {
    _hsq: any[];
  }
}

interface HubspotContextType {
  isHubspotLoaded: boolean;
  isFeedbackLoaded: boolean;
}

const HubspotContext = createContext<HubspotContextType>({
  isHubspotLoaded: false,
  isFeedbackLoaded: false,
});

export const useHubspotContext = () => useContext(HubspotContext);
/**
 * HubspotTracking Component
 * Works in both Next.js and React environments
 * @param {string} id - HubSpot tracking ID
 * @param {string} path - Current path for handler updates
 */
const HubspotTracking = ({ id, path, isUserLoading, email, children }) => {
  const hsqRef = useRef<any[]>();
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const lastTrackedPath = useRef<string>();
  const [isFeedbackLoaded, setIsFeedbackLoaded] = useState(false);
  useHubspotIdentify(email);
  /**
   * Safely initializes HubSpot queue
   */
  const initHsq = useCallback(() => {
    if (!window._hsq) {
      window._hsq = [];
    }
    hsqRef.current = window._hsq;
  }, []);

  const onLoad = () => {
    initHsq();
    setIsScriptLoaded(true);
    const feedbackScript = document.querySelector(
      `[src$='${process.env.NEXT_PUBLIC_HUBSPOT_FEEDBACK_SCRIPT_URL}']`,
    ) as HTMLScriptElement;
    if (feedbackScript) {
      feedbackScript.onload = () => setIsFeedbackLoaded(true);
    }
  };

  const trackPage = useCallback(
    (currentPath: string) => {
      if (!hsqRef.current) {
        initHsq();
      }

      if (lastTrackedPath.current !== currentPath) {
        hsqRef.current?.push(["setPath", currentPath]);
        hsqRef.current?.push(["trackPageView"]);
        lastTrackedPath.current = currentPath;
      }
    },
    [initHsq],
  );

  /**
   * Refreshes HubSpot handlers and tracks page view
   */
  const refreshHandlers = useCallback(() => {
    if (!hsqRef.current) {
      initHsq();
    }
    hsqRef.current?.push(["refreshPageHandlers"]);
  }, [initHsq]);

  /**
   * Load HubSpot script programmatically
   */
  const loadScript = useCallback(() => {
    if (scriptRef.current) return;

    const script = document.createElement("script");
    script.id = "hs-script-loader";
    script.onload = onLoad;
    script.src = `//js.hs-scripts.com/${id}.js`;
    script.async = true;
    scriptRef.current = script;
    document.body.appendChild(script);
  }, [id, initHsq]);

  /**
   * Effect to handle script loading
   */
  useEffect(() => {
    if (!isUserLoading && typeof window !== "undefined") {
      loadScript();
    }

    // Cleanup on unmount
    return () => {
      if (scriptRef.current && document.body.contains(scriptRef.current)) {
        document.body.removeChild(scriptRef.current);
      }
    };
  }, [isUserLoading, loadScript]);

  /**
   * Effect to refresh handlers when path changes
   * Only runs after script is loaded
   */
  useEffect(() => {
    if (isScriptLoaded && !isUserLoading) {
      trackPage(path);
      refreshHandlers();
    }
  }, [path, isUserLoading, isScriptLoaded, trackPage, refreshHandlers]);

  // Nothing to render - script is injected programmatically

  const value = useMemo(
    () => ({ isHubspotLoaded: isScriptLoaded, isFeedbackLoaded }),
    [isScriptLoaded, isFeedbackLoaded],
  );

  return <HubspotContext.Provider value={value}>{children}</HubspotContext.Provider>;
};

export default HubspotTracking;
